import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError, getSettings, patchSettings } from '../api/client'
import { updateProfile } from '../api/auth'
import { useAuth } from '../auth/AuthContext'
import OrphanAttachments from './settings/OrphanAttachments'
import type { SettingItem, SettingSection, SettingsResponse } from '../api/client'

/**
 * 设置页。
 *
 * 数据形态由服务端决定：`GET /api/settings` → `sections[] → items[]`。
 * 前端只负责"按 item 的控件类型渲染"，不硬编码有哪些设置项 —— 服务端加一项，这里自动出现。
 *
 * 编辑与保存：
 *   · 每个可编辑项有一个草稿值（draft），改动不立即提交；
 *   · **一张卡片一个「保存」按钮**，把所有改动一起 `PATCH /api/settings {changes}`；
 *   · 账号卡片的名字走 `PATCH /api/auth/profile`（它属于用户，不属于 AppSetting）；
 *   · 保存成功后重新拉取整个设置（服务端会规范化取值，避免前端和库里不一致），
 *     账号卡片额外调用 AuthContext.refresh() 让顶栏显示名同步。
 */

/** 编辑控件类型 */
type EditorKind = 'text' | 'number' | 'select' | 'json-textarea' | 'readonly'

/** 下拉选项规范化：兼容 `['a','b']` 与 `[{value,label}]` 两种服务端写法 */
function optionsOf(it: SettingItem): { value: string; label: string }[] {
  const raw = it.options
  if (raw !== undefined && raw.length > 0) {
    return raw.map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
  }
  // 服务端没给 options 时，至少让已知的三选一可编辑（降级，不编造其它选项）
  if (it.key === 'upload.filter_mode') {
    return [
      { value: 'none', label: '不限制' },
      { value: 'blacklist', label: '黑名单（禁止名单内的扩展名）' },
      { value: 'whitelist', label: '白名单（只允许名单内的扩展名）' },
    ]
  }
  return []
}

/** 由 item 推断用哪种控件：优先用服务端给的 kind，没有就把旧字段 type 映射过来 */
function editorKind(it: SettingItem): EditorKind {
  if (it.writable === false) return 'readonly'
  if (it.kind !== undefined) return it.kind
  switch (it.type) {
    case 'int':
      return 'number'
    case 'bool':
    case 'password':
      return 'readonly'
    case 'path':
    case 'string':
    default:
      return 'text'
  }
}

/** 服务端错误码 → 中文提示 */
const ERROR_TEXT: Record<string, string> = {
  invalid_max_bytes: '单文件上限必须是正整数（单位：字节）',
  invalid_filter_mode: '过滤模式只能是 none / blacklist / whitelist',
  invalid_filter_list: '过滤名单必须是 JSON 数组，例如 ["exe","dll"]',
  invalid_trusted_proxies: '可信代理列表格式不正确：请填写逗号分隔的 IP（如 10.0.0.5, 172.16.1.2）',
  invalid_attachments_dir: '附件目录不能为空，且必须是服务端可写的路径',
  // 服务端实际返回的错误码（与新增接口对齐）
  invalid_path: '路径格式不对：请填写盘符绝对路径，例如 D:\\Friday\\data\\attachments',
  invalid_number: '这个值必须是数字，且在允许范围内',
  invalid_enum: '请从下拉选项中选择一个合法值',
  invalid_json: '内容必须是合法的 JSON，例如 ["exe","dll"]',
  invalid_ip: '可信代理列表格式不正确：只能是逗号分隔的 IP 字面量（不支持网段）',
  too_many_items: '条目太多，请精简后再保存',
  unknown_key: '这个设置项不允许通过本接口修改',
  csrf_invalid: '会话校验失败，请刷新页面后重试',
  method_not_allowed: '请求方式不被支持',
  invalid_display_name: '显示名不能为空，且不超过 64 个字符',
  unknown_setting_key: '服务端不认识这个设置项（可能是版本不一致）',
  forbidden: '没有权限修改该设置',
  unauthorized: '登录已失效，请重新登录',
}

/** 把 ApiError 翻成中文；认不出的错误码原样带上，便于排查 */
function explainError(e: unknown): string {
  if (e instanceof ApiError) {
    const payload = e.payload as { error?: string; message?: string } | undefined
    const code = payload?.error ?? ''
    if (code !== '' && ERROR_TEXT[code] !== undefined) return ERROR_TEXT[code]
    if (payload?.message !== undefined && payload.message !== '') return payload.message
    if (code !== '') return `保存失败（${code}）`
    return `保存失败（HTTP ${e.status}）`
  }
  return `保存失败：${e instanceof Error ? e.message : String(e)}`
}

/** 草稿键：分区 + 项，避免不同卡片里同名 key 互相覆盖 */
function draftKey(sectionId: string, key: string): string {
  return `${sectionId}\u0000${key}`
}

/**
 * 卡片说明的兜底文案。
 *
 * 服务端没有给 `section.hint` 时用它 —— 这是**解释性文案**，不是数据，
 * 目的是让用户明白选项后果（尤其是安全卡片：伪造 X-Forwarded-For 会怎样）。
 */
const SECTION_HINT: Record<string, string> = {
  account: '账号信息属于当前登录用户。修改密码请使用右上角用户菜单。',
  upload:
    '附件目前只做存储配置，文件列尚未启用（等内核修复上传链路）。目录留空表示使用程序目录下的 data/attachments。',
  security:
    '可信代理：只有来自「本机」或该列表内 IP 的请求，服务端才会采信 X-Forwarded-For。' +
    '如果服务直接暴露在公网（前面没有反向代理），请不要把任何公网 IP 加进来 —— 否则客户端可以伪造该请求头，' +
    '冒充内网来源。首次初始化管理员正是依赖这条判定，填错等于把初始化入口暴露出去。',
}

export default function SettingsPage() {
  const { user, refresh } = useAuth()
  const [data, setData] = useState<SettingsResponse | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  /** 未保存的改动 */
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  /** 正在保存的分区 id */
  const [savingId, setSavingId] = useState('')
  /** 各分区的提示（成功/失败），按分区 id 存 */
  const [notices, setNotices] = useState<Record<string, { kind: 'ok' | 'warn'; text: string }>>({})
  /** 让"已保存"3 秒后自动消失 */
  const timers = useRef<Record<string, number>>({})

  const load = useCallback(async (): Promise<SettingsResponse | null> => {
    try {
      const d = await getSettings()
      setData(d)
      setError('')
      return d
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const bag = timers.current
    return () => {
      for (const t of Object.values(bag)) window.clearTimeout(t)
    }
  }, [])

  /** 给某个分区显示提示；kind='ok' 时 3 秒后自动消失 */
  const notify = useCallback((sectionId: string, kind: 'ok' | 'warn', text: string) => {
    setNotices((prev) => ({ ...prev, [sectionId]: { kind, text } }))
    window.clearTimeout(timers.current[sectionId])
    if (kind === 'ok') {
      timers.current[sectionId] = window.setTimeout(() => {
        setNotices((prev) => {
          if (prev[sectionId]?.text !== text) return prev
          const next = { ...prev }
          delete next[sectionId]
          return next
        })
      }, 3000)
    }
  }, [])

  const setDraft = useCallback((key: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [key]: value }))
  }, [])

  /** 某分区是否有未保存改动 */
  /**
   * 是否是"显示名"条目。
   *
   * 服务端的 key 是 `account.displayName`（带分区前缀），不能死写 'displayName'；
   * 优先看服务端给的 action，其次看 key 后缀。
   */
  const isProfileItem = (it: SettingItem): boolean =>
    it.action === 'profile' || it.key === 'displayName' || it.key.endsWith('.displayName')

  const dirtyOf = useCallback(
    (s: SettingSection): boolean =>
      s.items.some((it) => {
        if (editorKind(it) === 'readonly') return false
        const d = drafts[draftKey(s.id, it.key)]
        return d !== undefined && d !== it.value
      }),
    [drafts],
  )

  /** 保存一张卡片 */
  const saveSection = useCallback(
    async (s: SettingSection) => {
      if (savingId !== '') return

      // 账号卡片：显示名走 /api/auth/profile
      if (s.items.some((it) => isProfileItem(it) && editorKind(it) !== 'readonly')) {
        const nameItem = s.items.find((it) => isProfileItem(it))
        const next =
          nameItem === undefined ? undefined : drafts[draftKey(s.id, nameItem.key)]
        if (nameItem !== undefined && next !== undefined && next !== nameItem.value) {
          setSavingId(s.id)
          try {
            await updateProfile(next.trim())
            await load()
            await refresh()
            setDrafts((prev) => {
              const copy = { ...prev }
              if (nameItem !== undefined) delete copy[draftKey(s.id, nameItem.key)]
              return copy
            })
            notify(s.id, 'ok', '已保存')
          } catch (e) {
            notify(s.id, 'warn', explainError(e))
          } finally {
            setSavingId('')
          }
          return
        }
      }

      // 其余卡片：一次性提交本卡片的全部改动
      const changes: Record<string, string> = {}
      for (const it of s.items) {
        if (editorKind(it) === 'readonly') continue
        const d = drafts[draftKey(s.id, it.key)]
        if (d !== undefined && d !== it.value) changes[it.key] = d
      }
      if (Object.keys(changes).length === 0) {
        notify(s.id, 'ok', '没有改动')
        return
      }

      setSavingId(s.id)
      try {
        await patchSettings(changes)
        // 重新拉取：以服务端规范化后的值为准（例如 max_bytes 会去掉空白、filter_list 会重排 JSON）
        await load()
        setDrafts((prev) => {
          const copy = { ...prev }
          for (const k of Object.keys(changes)) delete copy[draftKey(s.id, k)]
          return copy
        })
        notify(s.id, 'ok', '已保存')
      } catch (e) {
        notify(s.id, 'warn', explainError(e))
      } finally {
        setSavingId('')
      }
    },
    [drafts, load, notify, refresh, savingId],
  )

  const sections = useMemo(() => data?.sections ?? [], [data])

  return (
    <div className="page">
      <div className="page__head">
        <h1 className="page__title">设置</h1>
        {user !== null && <span className="badge">{user.displayName !== '' ? user.displayName : user.username}</span>}
      </div>

      {data?.note !== undefined && <p className="page__note">{data.note}</p>}

      {loading && <p className="dim">加载中…</p>}
      {error !== '' && <pre className="kv kv--err">{error}</pre>}

      {data !== null && sections.length === 0 && <p className="dim">服务端未返回任何设置分区。</p>}

      {sections.map((s) => {
        const editable = s.items.some((it) => editorKind(it) !== 'readonly')
        const dirty = dirtyOf(s)
        const notice = notices[s.id]
        const busy = savingId === s.id

        return (
          <section className="card" key={s.id}>
            <h2 className="card__title">{s.title}</h2>
            {(() => {
              // 服务端给了就用服务端的；没给时按分区 id 用兜底说明（不改数据，只是文案）
              const hint = s.hint !== undefined && s.hint !== '' ? s.hint : (SECTION_HINT[s.id] ?? '')
              return hint === '' ? null : <p className="card__hint">{hint}</p>
            })()}

            {s.items.length === 0 ? (
              <p className="dim">条目待定 —— 该分区具体有哪些设置项尚未商定。</p>
            ) : (
              <dl className="kv">
                {s.items.map((it) => {
                  const kind = editorKind(it)
                  const dk = draftKey(s.id, it.key)
                  const value = drafts[dk] ?? it.value

                  return (
                    <div className="kv__row kv__row--field" key={it.key}>
                      <dt>
                        {it.label}
                        {it.hint !== undefined && it.hint !== '' && <span className="field-hint">{it.hint}</span>}
                      </dt>
                      <dd>
                        {kind === 'readonly' ? (
                          it.value !== '' ? (
                            <span>{it.value}</span>
                          ) : (
                            <span className="dim">未设置</span>
                          )
                        ) : kind === 'select' ? (
                          <select
                            className="settings-input"
                            value={value}
                            disabled={busy}
                            onChange={(e) => setDraft(dk, e.target.value)}
                          >
                            {optionsOf(it).map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : kind === 'json-textarea' ? (
                          <textarea
                            className="settings-textarea"
                            value={value}
                            rows={3}
                            spellCheck={false}
                            disabled={busy}
                            placeholder='例如 ["exe","dll"]'
                            onChange={(e) => setDraft(dk, e.target.value)}
                          />
                        ) : (
                          <input
                            className="settings-input"
                            type={kind === 'number' ? 'number' : 'text'}
                            value={value}
                            disabled={busy}
                            min={kind === 'number' ? 1 : undefined}
                            onChange={(e) => setDraft(dk, e.target.value)}
                          />
                        )}
                      </dd>
                    </div>
                  )
                })}
              </dl>
            )}

            {editable && (
              <div className="settings-actions">
                <button
                  type="button"
                  className="dialog__btn dialog__btn--primary"
                  disabled={busy || !dirty}
                  onClick={() => void saveSection(s)}
                >
                  {busy ? '保存中…' : '保存'}
                </button>
                {notice !== undefined && (
                  <span className={notice.kind === 'ok' ? 'settings-saved' : 'settings-error'}>{notice.text}</span>
                )}
              </div>
            )}

            {/* 不可编辑的卡片也可能给出提示（例如服务端返回的说明性条目） */}
            {!editable && notice !== undefined && (
              <p className={notice.kind === 'ok' ? 'settings-saved' : 'settings-error'}>{notice.text}</p>
            )}
          </section>
        )
      })}

      <OrphanAttachments />
    </div>
  )
}
